import { useEventListener, useRafFn } from "@vueuse/core";
import { type EffectScope, effectScope, nextTick } from "vue";

const AUTO_SCROLL_EDGE_PX = 60;
const AUTO_SCROLL_MAX_SPEED_PX_PER_SEC = 960;
const AUTO_SCROLL_MIN_SPEED_PX_PER_SEC = 120;

export type DragPointer = { x: number; y: number };

type DragTarget = {
  getScrollElement: () => HTMLElement | undefined;
  setScrollTop: (top: number) => void;
  updatePlacement: (pointer: DragPointer) => void;
};

const targetsByGroup = new Map<string, Set<DragTarget>>();
const lockedScrollTops = new Map<DragTarget, number>();
let dragPointer: DragPointer | null = null;
let endActiveDrag: (() => void) | null = null;
let dragScope: EffectScope | null = null;

const autoScroll = (target: DragTarget, el: HTMLElement, pointer: DragPointer, elapsedMs: number) => {
  const { top, bottom, left, right } = el.getBoundingClientRect();
  const distanceToTop = pointer.y - top;
  const distanceToBottom = bottom - pointer.y;
  const edge = Math.min(distanceToTop, distanceToBottom);
  if (edge < 0 || edge >= AUTO_SCROLL_EDGE_PX || pointer.x < left || pointer.x > right) {
    return false;
  }

  const proximity = (AUTO_SCROLL_EDGE_PX - edge) / AUTO_SCROLL_EDGE_PX;
  const speed =
    AUTO_SCROLL_MIN_SPEED_PX_PER_SEC +
    (AUTO_SCROLL_MAX_SPEED_PX_PER_SEC - AUTO_SCROLL_MIN_SPEED_PX_PER_SEC) * proximity;
  const step = (speed * elapsedMs) / 1000;
  const direction = distanceToTop < distanceToBottom ? -1 : 1;
  const lockedScrollTop = lockedScrollTops.get(target) ?? el.scrollTop;
  const previousScrollTop = el.scrollTop;
  const nextScrollTop = lockedScrollTop + step * direction;
  target.setScrollTop(nextScrollTop);
  lockedScrollTops.set(target, el.scrollTop === previousScrollTop ? previousScrollTop : nextScrollTop);
  return el.scrollTop !== previousScrollTop;
};

const autoScrollX = (pointer: DragPointer, elapsedMs: number) => {
  const el = document
    .elementsFromPoint(pointer.x, pointer.y)
    .find((e) => e.scrollWidth > e.clientWidth && ["auto", "scroll"].includes(getComputedStyle(e).overflowX));
  if (!el) {
    return false;
  }

  const { left, right } = el.getBoundingClientRect();
  const step = (AUTO_SCROLL_MAX_SPEED_PX_PER_SEC * elapsedMs) / 1000;
  const previousScrollLeft = el.scrollLeft;
  if (pointer.x - left < AUTO_SCROLL_EDGE_PX) {
    el.scrollLeft -= step;
  } else if (right - pointer.x < AUTO_SCROLL_EDGE_PX) {
    el.scrollLeft += step;
  }
  return el.scrollLeft !== previousScrollLeft;
};

const updatePlacements = (pointer: DragPointer) => {
  lockedScrollTops.forEach((_, target) => target.updatePlacement(pointer));
};

const pinScrollTops = () => {
  lockedScrollTops.forEach((lockedScrollTop, target) => {
    const el = target.getScrollElement();
    if (el && el.scrollTop !== lockedScrollTop) {
      target.setScrollTop(lockedScrollTop);
    }
  });
};

export const stopDragScroll = () => {
  dragScope?.stop();
  dragScope = null;
  lockedScrollTops.clear();
  dragPointer = null;
  endActiveDrag = null;
};

export const startDragScroll = (group: string, endDrag: () => void) => {
  stopDragScroll();
  endActiveDrag = endDrag;
  targetsByGroup.get(group)?.forEach((target) => {
    const el = target.getScrollElement();
    if (el) {
      lockedScrollTops.set(target, el.scrollTop);
    }
  });

  dragScope = effectScope(true);
  dragScope.run(() => {
    useEventListener(window, "dragover", (event: DragEvent) => {
      dragPointer = { x: event.clientX, y: event.clientY };
      updatePlacements(dragPointer);
    });
    useEventListener(window, "dragleave", (event: DragEvent) => {
      if (event.relatedTarget === null) {
        dragPointer = null;
      }
    });
    useEventListener(window, ["mouseup", "drop", "dragend", "blur"], () => endActiveDrag?.());
    useEventListener(window, "mousemove", (event: MouseEvent) => {
      if (event.buttons === 0) {
        endActiveDrag?.();
      }
    });

    useRafFn(({ delta }) => {
      const pointer = dragPointer;
      if (!pointer) {
        return;
      }

      let scrolled = false;
      lockedScrollTops.forEach((lockedScrollTop, target) => {
        const el = target.getScrollElement();
        if (!el) {
          return;
        }

        if (autoScroll(target, el, pointer, delta)) {
          scrolled = true;
        }
      });
      if (autoScrollX(pointer, delta)) {
        scrolled = true;
      }
      pinScrollTops();
      if (!scrolled) {
        return;
      }

      updatePlacements(pointer);
      nextTick(pinScrollTops);
    });
  });
};

export const registerDragTarget = (group: string, target: DragTarget) => {
  const targets = targetsByGroup.get(group) ?? new Set<DragTarget>();
  targets.add(target);
  targetsByGroup.set(group, targets);
};

export const unregisterDragTarget = (group: string, target: DragTarget) => {
  targetsByGroup.get(group)?.delete(target);
  if (lockedScrollTops.delete(target) && lockedScrollTops.size === 0) {
    endActiveDrag?.();
  }
};
